import { alpha, darken } from '@mui/material/styles';
import type { Theme } from '@mui/material/styles';
import type { SxProps } from '@mui/material/styles';

import type { IButtonLinkBlock } from 'src/interfaces/PageBlock';

export const BUTTON_BLOCK_THEME_COLORS = [
  'primary',
  'secondary',
  'success',
  'warning',
  'error',
  'info',
] as const;

type ButtonBlockThemeColor = (typeof BUTTON_BLOCK_THEME_COLORS)[number];

const HEX_COLOR = /^#[0-9a-fA-F]{6}$/;

export function isButtonBlockHexColor(value: string): boolean {
  return HEX_COLOR.test(value);
}

export function resolveButtonBlockColor(
  buttonColor: string | undefined,
  theme: Theme,
): string | undefined {
  if (!buttonColor?.trim()) return undefined;
  if (isButtonBlockHexColor(buttonColor)) return buttonColor;
  const palette = theme.palette[buttonColor as ButtonBlockThemeColor];
  if (palette && typeof palette === 'object' && 'main' in palette) {
    return palette.main as string;
  }
  return undefined;
}

export function buttonBlockColorSx(
  block: Pick<IButtonLinkBlock, 'variant' | 'buttonColor'>,
  theme: Theme,
): SxProps<Theme> {
  const resolved = resolveButtonBlockColor(block.buttonColor, theme);
  if (!resolved) return {};

  const variant = block.variant ?? 'contained';
  const contrast = theme.palette.getContrastText(resolved);

  switch (variant) {
    case 'contained':
      return {
        bgcolor: resolved,
        color: contrast,
        '&:hover': {
          bgcolor: darken(resolved, 0.12),
        },
      };
    case 'outlined':
      return {
        color: resolved,
        borderColor: resolved,
        '&:hover': {
          color: resolved,
          borderColor: resolved,
          bgcolor: alpha(resolved, 0.08),
        },
      };
    case 'text':
      return {
        color: resolved,
        '&:hover': {
          bgcolor: alpha(resolved, 0.08),
        },
      };
    default:
      return {};
  }
}
